import {
  Button, Calendar, CalendarCell, CalendarGrid,
  DateInput, DatePicker, DateSegment, Dialog, FieldError, Group, Heading, Label, Popover,

} from 'react-aria-components';
import { DateValue } from '@internationalized/date';
import customIcons from '../../utils/icons';

export default function InputDate({
  name, value, onChange, minimumDate, maximumDate,
}: Readonly<{ name: string, value?: DateValue | null, onChange?: (value: DateValue | null) => void, minimumDate?: DateValue, maximumDate?: DateValue }>) {
  const minimumSendDate = minimumDate || null;
  const maximumSendDate = maximumDate || null;
  const formatDateValue = (date: DateValue) => `${String(date.day).padStart(2, '0')}/${String(date.month).padStart(2, '0')}/${date.year}`;
  const datesUnavailable = minimumDate && maximumDate ? (date: DateValue) => (
    date.compare(minimumDate) < 0 || date.compare(maximumDate) > 0
  ) : undefined;

  return (
    <DatePicker
      shouldCloseOnSelect
      name={name}
      id={`${name}-date-picker`}
      className="ml-2 font-thin"
      validationBehavior="native"
      minValue={minimumSendDate}
      isDateUnavailable={datesUnavailable}
      maxValue={maximumSendDate}
      isRequired
      onChange={onChange}
      value={value}
    >
      <Label className="flex-none mr-2 p-1 text-sm" htmlFor={`${name}-date-picker`}>Date</Label>
      <Group className="flex items-center">
        <DateInput
          className="p-1 rounded-md align-middle bg-slate-50 border border-pipgrey-light text-piptext-body"
        >
          {(segment) => <DateSegment className="px-1 py-0.5 rounded focus:bg-blue-100 focus:outline-none" segment={segment} />}
        </DateInput>
        <Button>
          {customIcons.chevronDown}
        </Button>
      </Group>
      <FieldError className="text-pipstatus-alert font-normal">
        {({ validationDetails, validationErrors }) => {
          if (validationDetails.customError) {
            return validationErrors[0] || 'Please select a date.';
          }

          if (validationDetails.valueMissing) {
            return 'Please select a date.';
          }

          if (minimumSendDate && validationDetails.rangeUnderflow) {
            return `Value must be ${formatDateValue(minimumSendDate)} or later.`;
          }

          if (maximumSendDate && validationDetails.rangeOverflow) {
            return `Value must be ${formatDateValue(maximumSendDate)} or earlier.`;
          }

          if (validationDetails.badInput) {
            return 'Enter a valid date in DD/MM/YYYY format.';
          }

          return validationErrors.join(' ');
        }}
      </FieldError>
      <Popover>
        <Dialog>
          <Calendar
            className="w-fit bg-white"
            minValue={minimumSendDate}
            maxValue={maximumSendDate}
          >
            <header className="flex items-center justify-between mb-2">
              <Button slot="previous">
                {customIcons.chevronLeft}
              </Button>
              <Heading className="font-semibold text-lg" />
              <Button slot="next">
                {customIcons.chevronRight}
              </Button>
            </header>
            <CalendarGrid className="w-full">
              {(date) => (
                <CalendarCell
                  className="
                    w-9 h-9 outline-hidden cursor-default rounded-full flex items-center justify-center
                    outside-month:text-gray-300 hover:bg-gray-100 pressed:bg-gray-200
                    unavailable:text-pipstatus-alert
                    unavailable:line-through unavailable:opacity-75
                    data-invalid:ring-2 data-invalid:ring-pipstatus-alert/50
                    selected:bg-primary selected:text-white focus-visible:ring-3
                    ring-primary/70 ring-offset-2
                  "
                  date={date}
                />
              )}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </DatePicker>
  );
}
